import { MidiNote, midiToNote, tonalNoteToMidi, Note } from "./notes";
import { Midi, Scale as TonalScale } from "tonal";
import memoize from "lodash.memoize";
import { Range, rangeToMidi } from "./ranges";

type Degree = number;

export enum ScaleType {
  Major = "major",
  Minor = "minor",
}

export function isScaleType(scaleType: string): scaleType is ScaleType {
  if (typeof scaleType !== "string") {
    return false;
  }

  return Object.values(ScaleType).includes(scaleType as ScaleType);
}

export function maxScaleDegree(scaleType: ScaleType) {
  if (scaleType === "major") {
    return 8;
  }

  if (scaleType === "minor") {
    return 8;
  }

  throw new Error(`Invalid scale type ${scaleType}`);
}

export interface Scale {
  rootMidi: number;
  midiToDegree: Map<number, Degree>;
  type: ScaleType;
  degreeToMidiNote: Map<Degree, number>;
}

export const getScale = memoize(function getScale(
  rootMidi: number,
  scale: ScaleType
): Scale {
  const getDegree = scaleDegreeGenerator(rootMidi, scale);
  const degreeToMidiNote = new Map([
    [1, getDegree(1)],
    [2, getDegree(2)],
    [3, getDegree(3)],
    [4, getDegree(4)],
    [5, getDegree(5)],
    [6, getDegree(6)],
    [7, getDegree(7)],
    [8, getDegree(8)],
  ]);

  return {
    rootMidi,
    type: ScaleType.Major,
    degreeToMidiNote,
    midiToDegree: new Map(
      Array.from(degreeToMidiNote.entries()).map(([degree, note]) => [
        note,
        degree,
      ])
    ),
  };
});

export function getMidiNoteFromScaleByDegree(
  scale: Scale,
  degree: Degree
): number {
  const midiNote = scale.degreeToMidiNote.get(degree) ?? 0;

  if (!midiNote) {
    throw new Error(`Invalid degree ${degree} for scale ${scale.type}`);
  }

  return midiNote;
}

export function getNoteFromScaleByDegree(scale: Scale, degree: Degree): Note {
  return midiToNote(getMidiNoteFromScaleByDegree(scale, degree));
}

/**
 * Safely generates a function that returns the note of a given degree in a scale.
 */
export function scaleDegreeGenerator(
  rootMidi: number,
  scale: ScaleType
): (degree: number) => number {
  const noteName = Midi.midiToNoteName(rootMidi, {
    pitchClass: false,
    sharps: true,
  });

  const getter = TonalScale.degrees(`${noteName} ${scale}`);

  return (degree: number) => {
    return tonalNoteToMidi(getter(degree));
  };
}

/**
 * if the degrees of the scale are 1-3-5 this will find the max scale
 * root where all of those steps are withing the provided range
 */
export function getMaxScaleRootMidiForDegreesWithinRange({
  range,
  maxDegree,
  scaleType,
}: {
  maxDegree: Degree;
  range: Range;
  scaleType: ScaleType;
}): MidiNote {
  const [low, high] = rangeToMidi(range);

  const maxDegreeMidiNote = getMidiNoteFromScaleByDegree(
    getScale(low, scaleType),
    maxDegree
  );

  const semitonesAwayFromRangeHigh = high - maxDegreeMidiNote;

  return low + semitonesAwayFromRangeHigh + 1;
}
